import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Grid3x3, Images, X } from "lucide-react";

const SWIPE_THRESHOLD = 48;
const MAX_SCALE = 4;

/** 1 = next, -1 = previous, 0 = not a horizontal swipe. */
export function swipeDirection(dx, dy, threshold = SWIPE_THRESHOLD) {
  if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}

function photosOf(album) {
  const title = album?.title || "";
  return (album?.photos || [])
    .map((photo, index) => {
      if (typeof photo === "string") {
        return { id: `p-${index}`, url: photo, alt: title, caption: "" };
      }
      if (!photo || typeof photo !== "object" || !photo.url) return null;
      return {
        id: photo.id != null ? String(photo.id) : `p-${index}`,
        url: photo.url,
        alt: photo.alt || title,
        caption: photo.caption || "",
      };
    })
    .filter(Boolean);
}

function clampPan(x, y, scale) {
  if (scale <= 1) return { x: 0, y: 0 };
  const limitX = Math.max(80, window.innerWidth * 0.45) * (scale - 1);
  const limitY = Math.max(80, window.innerHeight * 0.45) * (scale - 1);
  return {
    x: Math.max(-limitX, Math.min(limitX, x)),
    y: Math.max(-limitY, Math.min(limitY, y)),
  };
}

const REST = { scale: 1, x: 0, y: 0 };

const PhotoViewer = ({ album, initialIndex = 0, onClose }) => {
  const photos = photosOf(album);
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(initialIndex, 0), Math.max(photos.length - 1, 0)),
  );
  const [mode, setMode] = useState("slide");
  const [transform, setTransform] = useState(REST);
  const [gesturing, setGesturing] = useState(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const transformRef = useRef(transform);
  const lastTap = useRef(0);
  transformRef.current = transform;

  const go = useCallback(
    (delta) => {
      setIndex((current) => {
        if (photos.length < 2) return current;
        return (current + delta + photos.length) % photos.length;
      });
    },
    [photos.length],
  );

  useEffect(() => {
    setIndex(Math.min(Math.max(initialIndex, 0), Math.max(photos.length - 1, 0)));
    setMode("slide");
    setTransform(REST);
  }, [album?.id, initialIndex, photos.length]);

  useEffect(() => {
    setTransform(REST);
  }, [index]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
      if (previousFocus && typeof previousFocus.focus === "function") {
        previousFocus.focus();
      }
    };
  }, [album?.id]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const items = [...root.querySelectorAll("button, [href], [tabindex]")].filter(
          (node) => !node.disabled && node.getAttribute("tabindex") !== "-1",
        );
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (mode !== "slide" || transformRef.current.scale > 1) {
        if (transformRef.current.scale > 1 && mode === "slide") {
          const step = 40;
          if (event.key === "ArrowLeft") {
            setTransform((current) => ({
              ...current,
              ...clampPan(current.x + step, current.y, current.scale),
              scale: current.scale,
            }));
          } else if (event.key === "ArrowRight") {
            setTransform((current) => ({
              ...current,
              ...clampPan(current.x - step, current.y, current.scale),
              scale: current.scale,
            }));
          }
        }
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        go(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        go(1);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [go, mode, onClose]);

  const zoomBy = (nextScale) => {
    const scale = Math.max(1, Math.min(MAX_SCALE, nextScale));
    if (scale <= 1.02) {
      setTransform(REST);
      return;
    }
    setTransform((current) => ({
      scale,
      ...clampPan(current.x, current.y, scale),
    }));
  };

  const toggleZoom = () => {
    setTransform((current) => (current.scale > 1 ? REST : { scale: 2, x: 0, y: 0 }));
  };

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignore when the browser rejects a synthetic pointer.
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        type: "pinch",
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: transformRef.current.scale,
      };
      setGesturing(true);
      return;
    }
    gesture.current = {
      type: "drag",
      x: event.clientX,
      y: event.clientY,
      originX: transformRef.current.x,
      originY: transformRef.current.y,
      scale: transformRef.current.scale,
      moved: false,
    };
  };

  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const current = gesture.current;
    if (!current) return;
    if (pointers.current.size >= 2 && current.type === "pinch") {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomBy(current.scale * (dist / current.dist));
      return;
    }
    if (current.type !== "drag") return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (Math.hypot(dx, dy) > 8) current.moved = true;
    if (current.scale > 1 && current.moved) {
      setGesturing(true);
      const pan = clampPan(current.originX + dx, current.originY + dy, current.scale);
      setTransform({ scale: current.scale, ...pan });
    }
  };

  const onPointerUp = (event) => {
    const current = gesture.current;
    pointers.current.delete(event.pointerId);
    if (pointers.current.size > 0) return;
    setGesturing(false);
    gesture.current = null;
    if (!current || current.type !== "drag") return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (current.scale <= 1) {
      const direction = swipeDirection(dx, dy);
      if (direction !== 0) {
        go(direction);
        return;
      }
    }
    if (!current.moved && Math.hypot(dx, dy) < 12) {
      const now = Date.now();
      if (now - lastTap.current < 400) {
        lastTap.current = 0;
        toggleZoom();
      } else {
        lastTap.current = now;
      }
    }
  };

  if (!album || photos.length === 0) return null;
  const photo = photos[index] || photos[0];
  const count = `${index + 1} of ${photos.length}`;
  const stop = (event) => event.stopPropagation();
  const guard = (event) => event.preventDefault();

  return (
    <div
      ref={dialogRef}
      className="smym-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={album.title}
      onClick={onClose}
    >
      <div className="smym-viewer__bar" onClick={stop}>
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold sm:text-lg">{album.title}</h2>
          <p className="text-sm opacity-80" aria-live="polite">
            {count}
            {album.date
              ? ` · ${new Date(album.date).toLocaleDateString("en-IN", {
                  month: "short",
                  year: "numeric",
                })}`
              : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className="smym-viewer__icon"
            aria-pressed={mode === "grid"}
            aria-label={mode === "grid" ? "Show slideshow" : "Show photo grid"}
            onClick={() =>
              setMode((current) => (current === "grid" ? "slide" : "grid"))
            }
          >
            {mode === "grid" ? (
              <Images className="h-5 w-5" aria-hidden />
            ) : (
              <Grid3x3 className="h-5 w-5" aria-hidden />
            )}
          </button>
          <button
            ref={closeRef}
            type="button"
            className="smym-viewer__icon"
            aria-label="Close photos"
            onClick={onClose}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>

      {mode === "grid" ? (
        <div className="smym-viewer__grid" onClick={stop}>
          {photos.map((item, photoIndex) => (
            <button
              key={item.id}
              type="button"
              className="smym-viewer__cell"
              aria-label={`Open photo ${photoIndex + 1} of ${photos.length}`}
              onClick={() => {
                setIndex(photoIndex);
                setMode("slide");
              }}
            >
              <img
                src={item.url}
                alt={item.alt || `${album.title} ${photoIndex + 1}`}
                draggable={false}
                onContextMenu={guard}
                onDragStart={guard}
              />
            </button>
          ))}
        </div>
      ) : (
        <div className="smym-viewer__stage">
          {photos.length > 1 && (
            <button
              type="button"
              className="smym-viewer__nav smym-viewer__nav--prev"
              aria-label="Previous photo"
              onClick={(event) => {
                stop(event);
                go(-1);
              }}
            >
              <ChevronLeft className="h-7 w-7" aria-hidden />
            </button>
          )}
          <div
            className="smym-viewer__frame"
            onClick={stop}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <img
              key={photo.id}
              src={photo.url}
              alt={photo.alt || `${album.title}, ${count}`}
              draggable={false}
              className={`smym-viewer__photo${gesturing ? " is-gesturing" : ""}`}
              style={{
                transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              }}
              onContextMenu={guard}
              onDragStart={guard}
            />
          </div>
          {photos.length > 1 && (
            <button
              type="button"
              className="smym-viewer__nav smym-viewer__nav--next"
              aria-label="Next photo"
              onClick={(event) => {
                stop(event);
                go(1);
              }}
            >
              <ChevronRight className="h-7 w-7" aria-hidden />
            </button>
          )}
          {photo.caption ? (
            <p className="smym-viewer__caption" onClick={stop}>
              {photo.caption}
            </p>
          ) : null}
        </div>
      )}

      {mode === "slide" && photos.length > 1 && (
        <div
          className="smym-viewer__thumbs"
          onClick={stop}
          aria-label="Photos in this album"
        >
          {photos.map((item, photoIndex) => (
            <button
              key={item.id}
              type="button"
              className="smym-viewer__thumb"
              aria-label={`Photo ${photoIndex + 1}`}
              aria-current={photoIndex === index ? "true" : undefined}
              onClick={() => setIndex(photoIndex)}
            >
              <img
                src={item.url}
                alt=""
                draggable={false}
                onContextMenu={guard}
                onDragStart={guard}
              />
            </button>
          ))}
        </div>
      )}

      <div hidden aria-hidden="true">
        {photos[index - 1] ? <img src={photos[index - 1].url} alt="" /> : null}
        {photos[index + 1] ? <img src={photos[index + 1].url} alt="" /> : null}
      </div>
    </div>
  );
};

export default PhotoViewer;
